<script lang="ts" setup>
  import { type PropType } from 'vue';
  import { useBreakpoints } from '@storefront/storefront';
  import type { Country, CustomerShippingAddress } from '@storefront/types';
  import { StoreLayout, AccountLayout } from '@storefront/components';
  import { useHttpClient, useConfirmation, useTranslator, useRoute } from '@othercommerce/foundation';

  defineOptions({
    layout: [StoreLayout, AccountLayout],
    inheritAttrs: false,
  });

  defineProps({
    addresses: { type: Array as PropType<CustomerShippingAddress[]>, required: true },
    countries: { type: Array as PropType<Country[]>, required: true },
    isMedicalProvider: { type: Boolean, required: true },
  });

  // TODO rid of useBreakpoints
  const breakpoints = useBreakpoints();
  const http = useHttpClient();
  const confirm = useConfirmation();
  const translator = useTranslator();
  const route = useRoute();

  function destroy(addressId: number) {
    confirm({
      callback: () => http.delete(route('web.account.address.shipping.destroy', { address: addressId })),
      title: translator.trans('SHOP.ACCOUNT.HEADER.SHIPPING_ADDRESS_DESTROY_CONFIRM'),
      description: translator.trans('SHOP.ACCOUNT.MESSAGE.SHIPPING_ADDRESS_DESTROY'),
      yes: translator.trans('SHOP.ACTION.DESTROY'),
      no: translator.trans('SHOP.ACTION.CANCEL'),
    }).catch(() => void (0));
  }
</script>
<template>
  <AccountMobileNavigation :href="$route('web.account.show')">
    {{ $t('SHOP.ACCOUNT.HEADER.SHIPPING_ADDRESSES') }}
  </AccountMobileNavigation>
  <SfTile class="account-delivery" large>
    <template v-if="breakpoints.lg" #title>
      <h1 class="account__heading">
        {{ $t('SHOP.ACCOUNT.HEADER.SHIPPING_ADDRESSES') }}
      </h1>
    </template>
    <template #headerAction>
      <div class="account-delivery__new">
        <SfButton :href="$route('web.account.address.shipping.create')" class="btn-add-adress" primary>
          <template #before>
            <SfIconPlus aria-hidden="true" />
          </template>
          <template #default>
            {{ $t('SHOP.ACTION.CREATE') }}
          </template>
        </SfButton>
      </div>
    </template>
    <template #default>
      <div v-if="isMedicalProvider" class="account-delivery__warning">
        {{ $t('SHOP.ACCOUNT.MESSAGE.MEDICAL_CONTRACTORS_ADDRESSES_NOTICE') }}
      </div>
      <div v-if="addresses.length > 0" class="account-delivery__addresses">
        <SfTile v-for="address in addresses" :default="address.default" :selected="address.default" class="account-delivery__address" shadow>
          <template v-if="address.default" #outer>
            <span class="account-delivery__address-default">{{ $t('SHOP.ACCOUNT.LABEL.ADDRESS_DEFAULT') }}</span>
          </template>
          <template #title>
            <h2 class="account-delivery__address-name">{{ address.company || address.name }}</h2>
            <span v-if="address.isMedical" :title="$t('SHOP.ACCOUNT.ARIA.MEDICAL_CONTRACTOR_IMPORTED_ADDRESS')" class="account-delivery__address-rpwdl">RPWDL</span>
          </template>
          <template #default>
            <address class="account-delivery__address-data">
              <p v-if="address.firstLine" class="account-delivery__address-first-line">{{ address.firstLine }}</p>
              <p v-if="address.secondLine" class="account-delivery__address-second-line">{{ address.secondLine }}</p>
              <p v-if="address.thirdLine" class="account-delivery__address-third-line">{{ address.thirdLine }}</p>
              <p v-if="address.countryCode" class="account-delivery__address-code">{{ address.countryCode }}</p>
              <p v-if="address.email" class="account-delivery__address-email">{{ address.email }}</p>
              <p v-if="address.phone" class="account-delivery__address-phone">{{ address.phone.rfc }}</p>
            </address>
          </template>
          <template #footer>
            <SfButton :aria-label="$t('SHOP.ACCOUNT.ARIA.SHIPPING_ADDRESS_DESTROY')" :disabled="isMedicalProvider && address.isMedical" apparent danger square transparent @click="destroy(address.id)">
              <template #before>
                <SfIconTrash aria-hidden="true" class="account-delivery__icon" />
              </template>
              <template #default>
                {{ $t('SHOP.ACTION.DESTROY') }}
              </template>
            </SfButton>
            <SfButton :aria-label="$t('SHOP.ACCOUNT.ARIA.SHIPPING_ADDRESS_EDIT')" :disabled="isMedicalProvider && address.isMedical" :href="$route('web.account.address.shipping.show', { address: address.id })">
              <template #before>
                <SfIconPen aria-hidden="true" />
              </template>
              <template #default>
                {{ $t('SHOP.ACTION.EDIT') }}
              </template>
            </SfButton>
          </template>
        </SfTile>
      </div>
      <SfDataPlaceholder v-else>
        {{ $t('SHOP.ACCOUNT.MESSAGE.SHIPPING_ADDRESS_PLACEHOLDER') }}
      </SfDataPlaceholder>
    </template>
  </SfTile>

  <RouterView />
</template>

<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .account-delivery {
    &__warning {
      font-size: 0.875rem;
    }

    &__addresses {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 2rem;
    }

    &__address {
      display: flex;
      position: relative;
    }

    &__address-name {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 80%;
      font-size: 1.25rem;
    }

    &__address-rpwdl {
      font-size: 0.75rem;
      font-weight: var(--font-weight-bold);
      color: var(--primary-color);
      margin-bottom: auto;
    }

    &__address-data p,
    &__address-data {
      font-size: 0.875rem;
    }
  }

  @include media.query(xxl) {
    .account-delivery .account-delivery__addresses {
      gap: 1rem;
    }
  }

  @include media.query(lg) {
    .account-delivery {
      &__new {
        display: flex;
        justify-content: flex-end;
        width: 100%;
      }

      &__address-name {
        font-size: 1.125rem;
      }

      &__address {
        .tile__outer {
          padding: 0.25rem 0.5rem;
        }
      }
    }
  }
</style>
